import { Link } from "react-router-dom";
import { ShieldCheck, Mail, MapPin, FileText, Receipt, Building2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useStoreInfo } from "@/lib/store";
import { Logo, LangSwitch } from "@/components/store/Header";
import { PaymentBadges } from "@/components/store/PaymentBadges";

const InfoRow = ({ icon: Icon, label, value, testid, href }) => (
  <li className="flex items-start gap-2 text-sm">
    <Icon className="w-4 h-4 mt-0.5 text-white/40 shrink-0" />
    <span className="text-white/50">{label}:</span>
    {href && value ? <a data-testid={testid} href={href} className="text-white/85 hover:text-white" dir="ltr">{value}</a>
      : <span data-testid={testid} className={value ? "text-white/85" : "text-white/40 italic"} dir="auto">{value || "—"}</span>}
  </li>
);

export default function Footer() {
  const { t, pick } = useI18n();
  const s = useStoreInfo();
  const name = [s?.store_name || "ONEX", pick(s?.business_name)].filter(Boolean).join(" — ");
  const cols = [
    [t("footer.shop"), [["/products", t("nav.products")], ["/products?offer=1", t("nav.offers")], ["/products?sort=best", t("home.best_title")], ["/track", t("nav.track")]]],
    [t("footer.legal"), [["/privacy", t("legal.privacy_title")], ["/terms", t("legal.terms_title")], ["/policy", t("footer.refund_policy")], ["/shipping-policy", t("legal.shipping_title")]]],
    [t("footer.help"), [["/support", t("footer.customer_support")], ["/account/warranty", t("footer.warranty")], ["/account/returns", t("footer.returns")], ["/account", t("footer.my_account")]]],
  ];
  return (
    <footer className="print:hidden bg-[#0B0D0F] text-white mt-24" data-testid="site-footer">
      <div className="container-x py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
        <div className="space-y-5">
          <Logo light />
          <p className="text-sm text-white/60 max-w-xs leading-relaxed">{t("footer.about")}</p>
          <ul className="space-y-2.5" data-testid="footer-business-info">
            <InfoRow icon={Building2} label={t("footer.trade_name")} value={pick(s?.business_name)} testid="footer-trade-name" />
            <InfoRow icon={FileText} label={t("footer.cr")} value={s?.cr_number} testid="footer-cr-number" />
            <InfoRow icon={Receipt} label={t("footer.vat")} value={s?.vat_number} testid="footer-vat-number" />
            <InfoRow icon={Receipt} label={t("footer.tin")} value={s?.tin} testid="footer-tin" />
            <InfoRow icon={Mail} label={t("footer.email")} value={s?.support_email} href={`mailto:${s?.support_email}`} testid="footer-support-email" />
            <InfoRow icon={MapPin} label={t("footer.address")} value={pick(s?.address)} testid="footer-address" />
          </ul>
        </div>
        {cols.map(([title, links]) => (
          <div key={title}>
            <h4 className="text-sm font-semibold mb-4">{title}</h4>
            <ul className="space-y-3">{links.map(([to, l]) => <li key={to}><Link data-testid={`footer-link${to.replace(/[/?=]/g, "-")}`} to={to} className="text-sm text-white/60 hover:text-white transition-colors">{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="container-x pb-8 flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="text-xs text-white/50">{t("footer.we_accept")}</span>
        <PaymentBadges methods={s?.payment_methods} />
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col sm:flex-row gap-4 items-center justify-between text-xs text-white/50">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} <bdi>{name}</bdi>. {t("footer.rights")}</span>
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2563EB]" />{t("footer.secure")}</span>
          <LangSwitch className="text-white/70" />
        </div>
      </div>
    </footer>
  );
}
